import React, { useState, useEffect } from 'react';
import './SubscriptionSelectionPage.css';
import AppHeader from './AppHeader';
import ConfirmationModal from './ConfirmationModal';
import PaymentMethodModal from './PaymentMethodModal';
import EmailModal from './EmailModal';
import ReferralModal from './ReferralModal';
import { triggerHaptic, openExternalLink } from '../services/telegram';
import { getPaymentMethod, setPaymentMethod as savePaymentMethod } from '../utils/paymentMethod';
import Toast from './Toast';
import apiService from '../services/api';

const MIN_PRICE_RUB = 3;

const PLANS_PRO = [
  { id: '1m', months: 1, label: '1 месяц', price: 185, pricePerMonth: 185 },
  { id: '3m', months: 3, label: '3 месяца', price: 519, pricePerMonth: 173 },
  { id: '6m', months: 6, label: '6 месяцев', price: 969, pricePerMonth: 160 },
  { id: '12m', months: 12, label: '1 год', price: 1769, pricePerMonth: 147 },
];


// const PLANS_PRO = [
//   { id: '1m', months: 1, label: '1 месяц', price: 10, pricePerMonth: 185 },
//   { id: '3m', months: 3, label: '3 месяца', price: 519, pricePerMonth: 173 },
//   { id: '6m', months: 6, label: '6 месяцев', price: 969, pricePerMonth: 160 },
//   { id: '12m', months: 12, label: '1 год', price: 1769, pricePerMonth: 147 },
// ];

function SubscriptionSelectionPage({ onBack, tariffId, userId, onOpenReferral, trialEndDate = null }) {
  const plans = PLANS_PRO;
  const [selectedPlan, setSelectedPlan] = useState(plans[1]);
  useEffect(() => {
    setSelectedPlan(plans[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffId]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isMethodModalOpen, setIsMethodModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [paymentMethod, setPaymentMethodState] = useState('card');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info', duration: 5000 });
  const [bonusBalance, setBonusBalance] = useState(0);

  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [friendsInvited, setFriendsInvited] = useState(0);
  const [referralRewards, setReferralRewards] = useState([]);
  const [isReferralLoading, setIsReferralLoading] = useState(false);

  useEffect(() => {
    if (!isReferralOpen || !userId) return;
    let cancelled = false;
    setIsReferralLoading(true);
    (async () => {
      try {
        const [linkRes, countRes, rewardsRes] = await Promise.allSettled([
          apiService.getReferralLink(String(userId)),
          apiService.getReferralCount(String(userId)),
          apiService.getReferralRewards(String(userId)),
        ]);
        if (cancelled) return;
        setReferralLink(linkRes.status === 'fulfilled' ? (linkRes.value?.referral_link || '') : '');
        setFriendsInvited(countRes.status === 'fulfilled' ? (Number(countRes.value?.referral_count) || 0) : 0);
        setReferralRewards(
          rewardsRes.status === 'fulfilled' && Array.isArray(rewardsRes.value?.rewards)
            ? rewardsRes.value.rewards : []
        );
      } catch (_) {
        if (!cancelled) { setReferralLink(''); setFriendsInvited(0); setReferralRewards([]); }
      } finally {
        if (!cancelled) setIsReferralLoading(false);
      }
    })();
    return () => { cancelled = true; setIsReferralLoading(false); };
  }, [isReferralOpen, userId]);

  useEffect(() => {
    if (!userId) return;
    apiService.getBonusBalance(userId)
      .then(data => setBonusBalance(Math.max(0, Number(data?.bonus_balance) || 0)))
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    setPaymentMethodState(getPaymentMethod());
  }, []);

  // Funnel 1 tracking: user opened the subscription page
  useEffect(() => {
    if (!userId) return;
    apiService.trackEvent({ funnel_id: 1, step: 'buy_page_opened', user_id: userId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayClick = () => {
    triggerHaptic();
    apiService.trackEvent({ funnel_id: 1, step: 'buy_clicked', user_id: userId });
    setIsConfirmOpen(true);
  };

  const getDiscountedPrice = (origPrice) =>
    bonusBalance > 0 ? Math.max(MIN_PRICE_RUB, origPrice - bonusBalance) : origPrice;

  const discountedSelected = getDiscountedPrice(selectedPlan.price);

  const handleConfirmPay = async ({ autorenewConsent = false } = {}) => {
    setIsConfirmOpen(false);
    if (!userId || !selectedPlan || isCheckingOut) return;
    setIsCheckingOut(true);
    apiService.trackEvent({ funnel_id: 1, step: 'payment_started', user_id: userId });
    // TODO: restore email input when ready. Stub email is used only for payment
    // processing and is NOT saved to localStorage or database.
    const STUB_EMAIL = 'test@gmail.com';
    const durationDays = Math.round(selectedPlan.months * 30.44);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const successBase =
      (origin && origin.startsWith('https://') ? origin : (process.env.REACT_APP_SUCCESS_BASE_URL || '')).replace(/\/$/, '');
    let returnUrl = `${successBase}/success.html`;
    if (apiBase) {
      const joiner = returnUrl.includes('?') ? '&' : '?';
      returnUrl = `${returnUrl}${joiner}api=${encodeURIComponent(apiBase)}`;
    }
    try {
      const res = await apiService.subscriptionCheckout({
        userid: userId,
        tariff_plan: 'pro',
        duration_days: durationDays,
        amount: String(selectedPlan.price),
        return_url: returnUrl,
        payment_method: paymentMethod,
        email: STUB_EMAIL,
        autorenew_consent: autorenewConsent,
        autorenew_terms_version: '2026-08-12',
      });
      if (res && res.confirmation_url) {
        try {
          if (res.payment_id && typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('beznet_pending_payment_id', String(res.payment_id));
          }
        } catch (_) {}
        apiService.trackEvent({ funnel_id: 1, step: 'payment_url_received', user_id: userId });
        openExternalLink(res.confirmation_url);
      } else {
        setToast({ isOpen: true, message: 'Не удалось получить ссылку на оплату', type: 'error', duration: 6000 });
      }
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : '';
      if (/Load failed|Нет связи|CORS|fetch|network|Failed to fetch|Failed to load|status 0|TypeError.*fetch/i.test(msg)) {
        setToast({ isOpen: true, message: 'Не удалось подключиться к серверу. Проверьте интернет.', type: 'error', duration: 7000 });
      } else {
        setToast({ isOpen: true, message: msg || 'Ошибка при создании платежа', type: 'error', duration: 7000 });
      }
    } finally {
      setIsCheckingOut(false);
    }
  };

  const formatDateEnd = (months) => {
    // If user is on trial, start the date from trial end (remaining days are added by backend)
    const base = trialEndDate && new Date(trialEndDate) > new Date() ? new Date(trialEndDate) : new Date();
    const d = new Date(base);
    d.setDate(d.getDate() + Math.round(months * 30.44));
    const day = d.getDate();
    const monthsRu = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    return `${day} ${monthsRu[d.getMonth()]} ${d.getFullYear()}`;
  };

  return (
    <div className="subscription-selection-page">
      <div className="subscription-selection-background"></div>
      <AppHeader title="Rocket VPN" />
      <div className="subscription-selection-content">
        <div className="subscription-selection-body">
          <div className="subscription-selection-panel">
            <h1 className="subscription-selection-title">Выбор подписки</h1>
            {bonusBalance > 0 ? (
              <div className="subscription-bonus-banner">
                <span className="subscription-bonus-icon">🎁</span>
                <div className="subscription-bonus-text">
                  <span className="subscription-bonus-title">У вас {bonusBalance} бонусных рублей</span>
                  <span className="subscription-bonus-sub">Автоматически применяются при оплате</span>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="subscription-referral-banner"
                onClick={() => { triggerHaptic(); setIsReferralOpen(true); }}
              >
                <span className="subscription-referral-icon">🚀</span>
                <div className="subscription-referral-text">
                  <span className="subscription-referral-title">Пригласи друга — получи 100 ₽</span>
                  <span className="subscription-referral-sub">Друг получит 7 дней VPN бесплатно</span>
                </div>
                <span className="subscription-referral-arrow">›</span>
              </button>
            )}
            <div className="subscription-selection-grid">
            {plans.map((plan) => {
              const discounted = getDiscountedPrice(plan.price);
              const hasDiscount = bonusBalance > 0 && discounted < plan.price;
              return (
              <button
                key={plan.id}
                type="button"
                className={`subscription-plan-card ${selectedPlan.id === plan.id ? 'subscription-plan-card-selected' : ''}`}
                onClick={() => { triggerHaptic(); setSelectedPlan(plan); }}
              >
                <span className="subscription-plan-label">{plan.label}</span>
                <span className="subscription-plan-badge">ПОПУЛЯРНЫЙ</span>
                {hasDiscount ? (
                  <>
                    <span className="subscription-plan-price-original">{plan.price} Р</span>
                    <span className="subscription-plan-price subscription-plan-price-discounted">{discounted} Р</span>
                  </>
                ) : (
                  <span className="subscription-plan-price">{plan.price} Р</span>
                )}
                {plan.pricePerMonth != null && (
                  <span className="subscription-plan-price-per-month">{plan.pricePerMonth} Р / мес</span>
                )}
              </button>
              );
            })}
            </div>
            <button
              type="button"
              className="subscription-selection-pay-button"
              onClick={handlePayClick}
              disabled={isCheckingOut}
            >
              {isCheckingOut ? 'Создаём платёж…' : (
                <>
                  Оплатить {discountedSelected} Р
                  {bonusBalance > 0 && discountedSelected < selectedPlan.price && (
                    <span className="subscription-pay-bonus-hint"> (было {selectedPlan.price} Р)</span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        plan={selectedPlan}
        tariffId={tariffId}
        formatDateEnd={formatDateEnd}
        userId={userId}
        paymentMethod={paymentMethod}
        onChangePaymentMethod={() => setIsMethodModalOpen(true)}
        onConfirm={handleConfirmPay}
      />

      <PaymentMethodModal
        isOpen={isMethodModalOpen}
        currentMethod={paymentMethod}
        onClose={() => setIsMethodModalOpen(false)}
        onSelect={(methodId) => {
          setPaymentMethodState(methodId);
          savePaymentMethod(methodId);
          setIsMethodModalOpen(false);
        }}
      />

      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        userId={userId}
        plan={selectedPlan}
        tariffId={'pro'}
        paymentMethod={paymentMethod}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        duration={toast.duration ?? 5000}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />

      <ReferralModal
        isOpen={isReferralOpen}
        onClose={() => setIsReferralOpen(false)}
        referralLink={referralLink}
        friendsInvited={friendsInvited}
        rewards={referralRewards}
        isLoading={isReferralLoading}
      />
    </div>
  );
}

export default SubscriptionSelectionPage;
